import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { toast } from 'sonner';
import {
  ArrowLeft, RefreshCw, AlertCircle, AlertTriangle,
  CheckCircle2, Info, Loader2, ChevronDown, ChevronRight,
} from 'lucide-react';
import { readResponse } from '../utils/fetchSafe';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SEVERITY_STYLE = {
  high:   { tag: 'bg-red-100 text-red-700 border-red-200',       icon: AlertCircle,    iconClass: 'text-red-600'   },
  medium: { tag: 'bg-amber-100 text-amber-700 border-amber-200', icon: AlertTriangle,  iconClass: 'text-amber-600' },
  low:    { tag: 'bg-sky-100 text-sky-700 border-sky-200',       icon: Info,           iconClass: 'text-sky-600'   },
};

const ACTION_ROUTES = {
  legacy_employee_names: { label: 'Open Normalize Names', to: '/setup/normalize-names' },
  stale_customer_ids:    { label: 'Open Repair Customer IDs', to: '/setup/repair-customer-ids' },
};

const SampleTable = ({ rows }) => {
  if (!rows || rows.length === 0) return null;
  const cols = Array.from(
    rows.reduce((acc, r) => {
      Object.keys(r).forEach((k) => acc.add(k));
      return acc;
    }, new Set())
  );
  return (
    <div className="overflow-x-auto border border-gray-200 rounded">
      <table className="w-full text-xs">
        <thead className="bg-gray-50 text-gray-600">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left p-2 font-normal">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-gray-100">
              {cols.map((c) => (
                <td key={c} className="p-2 text-gray-700 font-mono">
                  {Array.isArray(r[c]) ? r[c].join(', ') : (r[c] === null || r[c] === undefined ? '' : String(r[c]))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const CheckCard = ({ check }) => {
  const [open, setOpen] = useState(check.count > 0 && check.severity === 'high');
  const sev = SEVERITY_STYLE[check.severity] || SEVERITY_STYLE.low;
  const Icon = sev.icon;
  const action = ACTION_ROUTES[check.key];
  const navigate = useNavigate();
  const clean = check.count === 0;

  return (
    <div className="bg-white rounded shadow border border-gray-100" data-testid={`health-check-${check.key}`}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-3 p-4 hover:bg-gray-50 text-left"
      >
        {clean ? (
          <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" />
        ) : (
          <Icon className={`w-5 h-5 flex-shrink-0 ${sev.iconClass}`} />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-800">{check.label}</span>
            {!clean && (
              <span className={`text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded border ${sev.tag}`}>
                {check.severity}
              </span>
            )}
          </div>
          {check.fix_hint && (
            <p className="text-xs text-gray-500 mt-0.5">{check.fix_hint}</p>
          )}
        </div>
        <span
          className={`text-sm font-medium px-2.5 py-1 rounded ${
            clean ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-800'
          }`}
          data-testid={`health-count-${check.key}`}
        >
          {check.count}
        </span>
        {!clean && (open ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />)}
      </button>

      {open && !clean && (
        <div className="px-4 pb-4 space-y-3">
          {action && (
            <button
              onClick={() => navigate(action.to)}
              className="bg-[#5BC0DE] hover:bg-[#46b8da] text-white text-xs px-3 py-1.5 rounded"
              data-testid={`health-action-${check.key}`}
            >
              {action.label} →
            </button>
          )}
          <div className="text-xs text-gray-500">
            Showing {Math.min(check.samples?.length || 0, 25)} of {check.count} {check.count === 1 ? 'sample' : 'samples'}
          </div>
          <SampleTable rows={check.samples} />
        </div>
      )}
    </div>
  );
};

const DataHealth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const runScan = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/admin/data-health`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { data } = await readResponse(res);
      if (!res.ok) throw new Error(data?.detail || 'Scan failed');
      setReport(data);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runScan(); }, []);

  const summary = useMemo(() => {
    if (!report) return null;
    const counts = { high: 0, medium: 0, low: 0, clean: 0 };
    report.checks.forEach((c) => {
      if (c.count === 0) counts.clean += 1;
      else counts[c.severity] = (counts[c.severity] || 0) + 1;
    });
    return counts;
  }, [report]);

  return (
    <Layout>
      <div className="p-4 lg:p-8" data-testid="data-health-page">
        <div className="mb-6 flex items-end justify-between gap-4">
          <div>
            <h2 className="text-lg text-[#5B8DB8] font-light">Administration</h2>
            <h1 className="text-3xl text-gray-700 font-light italic">Data Health</h1>
            <p className="text-sm text-gray-500 mt-1 max-w-3xl">
              Read-only scan of the database for legacy issues: trailing-zero invoice numbers, malformed dates,
              orphaned references, denormalization drift, name-format variants, missing required fields, and
              stale customer IDs. Re-run any time. Each section links to the matching repair tool when one exists.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={runScan}
              disabled={loading}
              className="bg-[#5BC0DE] hover:bg-[#46b8da] disabled:opacity-50 text-white px-3 py-2 rounded text-sm flex items-center gap-2"
              data-testid="health-rescan-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              {loading ? 'Scanning…' : 'Re-run scan'}
            </button>
            <button
              onClick={() => navigate('/setup')}
              className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded border border-gray-200"
            >
              <ArrowLeft className="w-4 h-4" /> Back to Setup
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded p-4 mb-4 text-sm" data-testid="health-error">
            {error}
          </div>
        )}

        {report && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="health-summary">
              <div className="bg-white rounded shadow p-4">
                <div className="text-xs text-gray-500 uppercase">Projects</div>
                <div className="text-2xl font-light text-gray-700">{report.totals.projects}</div>
              </div>
              <div className="bg-white rounded shadow p-4">
                <div className="text-xs text-gray-500 uppercase">Customers</div>
                <div className="text-2xl font-light text-gray-700">{report.totals.customers}</div>
              </div>
              <div className="bg-white rounded shadow p-4">
                <div className="text-xs text-gray-500 uppercase">Users</div>
                <div className="text-2xl font-light text-gray-700">{report.totals.employees}</div>
              </div>
              <div className="bg-white rounded shadow p-4">
                <div className="text-xs text-gray-500 uppercase">Issues found</div>
                <div className="flex items-baseline gap-2 text-sm">
                  <span className="text-red-600 font-medium text-lg">{summary?.high || 0}</span><span className="text-gray-400">high</span>
                  <span className="text-amber-600 font-medium text-lg ml-2">{summary?.medium || 0}</span><span className="text-gray-400">med</span>
                  <span className="text-sky-600 font-medium text-lg ml-2">{summary?.low || 0}</span><span className="text-gray-400">low</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {report.checks.map((c) => (
                <CheckCard key={c.key} check={c} />
              ))}
            </div>

            <div className="text-xs text-gray-400 mt-6">
              Scanned at {new Date(report.scanned_at).toLocaleString()}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default DataHealth;
